import dayjs from 'dayjs';
import { Provider, ProviderAvailability } from '@types';
import { convertTimeToDayjs } from '@utils/datetime/convertTimeToDayjs';
import { TimeBlock } from '../context/utils';

type VisitBlock = {
  provider: Provider;
  time: string;
};

type Params = {
  availabilities: ProviderAvailability[];
  timeBlock?: TimeBlock;
};

type ReturnValue = {
  firstVisit: VisitBlock | null;
  secondVisit: VisitBlock | null;
  thirdVisit: VisitBlock | null;
};

export function getVisitBlocks({
  availabilities,
  timeBlock,
}: Params) {
  const normalizedTimeSlots = normalizeAvailabilitiesToSlots(
    availabilities,
    timeBlock,
  );

  const returnValue: ReturnValue = {
    firstVisit: null,
    secondVisit: null,
    thirdVisit: null,
  };

  const existingProviderIds = new Set<string>();

  const earliestVisit = getEarliestAppointment(normalizedTimeSlots);

  if (earliestVisit) {
    returnValue.firstVisit = {
      provider: earliestVisit.provider,
      time: earliestVisit.time,
    };

    existingProviderIds.add(earliestVisit.provider.id);
  }

  const latestVisit = getLatestAppointment(
    normalizedTimeSlots,
    existingProviderIds,
  );

  if (latestVisit) {
    returnValue.thirdVisit = {
      provider: latestVisit.provider,
      time: latestVisit.time,
    };

    existingProviderIds.add(latestVisit.provider.id);
  }

  const middleAppointment = getMiddleAppointment(
    normalizedTimeSlots,
    existingProviderIds,
  );

  if (middleAppointment) {
    returnValue.secondVisit = {
      provider: middleAppointment.provider,
      time: middleAppointment.time,
    };

    existingProviderIds.add(middleAppointment.provider.id);
  }

  return returnValue;
}

type NormalizedTimeSlot = {
  provider: Provider;
  time: string;
};

function normalizeAvailabilitiesToSlots(
  availabilities: ProviderAvailability[],
  timeBlock?: TimeBlock,
): NormalizedTimeSlot[] {
  const oneHourFromNow = dayjs().add(1, 'hour');
  const tomorrowBase = dayjs().add(1, 'day').startOf('day');
  const tomorrowTimeBlockStart = tomorrowBase.set('hour', 9);
  const tomorrowTimeBlockEnd = tomorrowBase.set('hour', 16);

  const normalized = availabilities.reduce<NormalizedTimeSlot[]>(
    (memo, availability) => {
      const { provider, timeSlotCollection } = availability;

      timeSlotCollection.forEach((timeSlot) => {
        const { times } = timeSlot;

        // NOTE: Filter out times that are not within the desired time block
        times.forEach((time) => {
          if (timeBlock === 'today') {
            const appointmentTime = convertTimeToDayjs(time);

            if (appointmentTime.isBefore(oneHourFromNow)) {
              return;
            }
          }

          if (timeBlock === 'tomorrow') {
            const [hour, minute] = time.split(':').map(Number);
            const appointmentTime = tomorrowBase
              .set('hour', hour)
              .set('minute', minute);

            if (
              appointmentTime.isBefore(tomorrowTimeBlockStart) ||
              appointmentTime.isAfter(tomorrowTimeBlockEnd)
            ) {
              return;
            }
          }

          memo.push({
            provider,
            time,
          });
        });
      });

      return memo;
    },
    [],
  );

  const sorted = normalized.sort((a, b) => {
    const aTime = Number(a.time.replace(':', ''));
    const bTime = Number(b.time.replace(':', ''));

    return aTime - bTime;
  });

  return sorted;
}

function getEarliestAppointment(normalizedTimeSlots: NormalizedTimeSlot[]) {
  // NOTE: Availabilities are sorted by time, first element is the one of the earliest availabilities
  return normalizedTimeSlots[0] || null;
}

function getMiddleAppointment(
  normalizedTimeSlots: NormalizedTimeSlot[],
  existingProviderIds: Set<string>,
) {
  if (normalizedTimeSlots.length === 0) {
    return null;
  }

  const middleIndex = Math.floor(normalizedTimeSlots.length / 2);

  let left = middleIndex;
  let right = middleIndex + 1;

  while (left >= 0 || right < normalizedTimeSlots.length) {
    if (left >= 0) {
      const candidate = normalizedTimeSlots[left];

      if (!existingProviderIds.has(candidate.provider.id)) {
        return candidate;
      }

      left--;
    }

    if (right < normalizedTimeSlots.length) {
      const candidate = normalizedTimeSlots[right];

      if (!existingProviderIds.has(candidate.provider.id)) {
        return candidate;
      }

      right++;
    }
  }

  return null;
}

function getLatestAppointment(
  normalizedTimeSlots: NormalizedTimeSlot[],
  existingProviderIds: Set<string>,
) {
  // NOTE: availabilities are sorted by time; iterate from the end to find one of the latest availabilities
  for (let i = normalizedTimeSlots.length - 1; i >= 0; i--) {
    const { provider, time } = normalizedTimeSlots[i];

    if (!existingProviderIds.has(provider.id)) {
      return { provider, time };
    }
  }

  return null;
}
